import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getProjectBudget, resyncProjectBudget, setProjectBudget } from '../../api/client'
import Modal from '../Modal'
import BudgetBar from './BudgetBar'
import { formatCLP, formatNumber } from './format'

const STATE_STYLES = {
  approved: 'bg-blue-50 text-blue-700',
  sap: 'bg-blue-50 text-blue-700',
  ordered: 'bg-amber-50 text-amber-700',
  received: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-gray-100 text-gray-600',
}

export default function BudgetTab({ projectId, canEdit }) {
  const { t } = useTranslation(['engineering', 'common'])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await getProjectBudget(projectId))
    } catch (err) {
      setError(err.response?.data?.detail || t('budget.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [projectId, t])

  useEffect(() => { load() }, [load])

  const handleResync = async () => {
    setBusy(true)
    try { setData(await resyncProjectBudget(projectId)) }
    finally { setBusy(false) }
  }

  if (loading && !data) {
    return <div className="bg-white rounded-xl border border-gray-200 py-14 text-center text-sm text-gray-400">…</div>
  }
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 py-10 text-center space-y-3">
        <p className="text-sm text-red-600">{error}</p>
        <button onClick={load} className="text-sm text-gray-600 underline">
          {t('budget.retry')}
        </button>
      </div>
    )
  }

  const { hours, money, rates } = data
  const staleRates = rates.filter(r => r.stale && !r.missing)
  const usedRates = new Set(money.lines.map(l => l.source_currency).filter(c => c !== 'CLP'))

  return (
    <div className="space-y-4">
      {/* ── The two budgets ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{t('budget.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('budget.subtitle')}</p>
          </div>
          {canEdit && (
            <button onClick={() => setEditing(true)}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 whitespace-nowrap">
              {t('budget.edit')}
            </button>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <BudgetBar side={hours} kind="hours" />
            <p className="text-[11px] text-gray-400">{t('budget.hoursUsedHint')}</p>
          </div>
          <div className="space-y-2">
            <BudgetBar side={money} kind="money" />
            <p className="text-[11px] text-gray-400">{t('budget.moneyUsedHint')}</p>
          </div>
        </div>
      </div>

      {/* ── Orders the panel could not count ────────────────────────── */}
      {money.missing_rate.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
          <p className="text-sm font-semibold text-amber-900">{t('budget.missingRateTitle')}</p>
          <p className="text-xs text-amber-800">{t('budget.missingRateBody')}</p>
          <ul className="text-xs text-amber-900 space-y-0.5">
            {money.missing_rate.map(o => (
              <li key={o.id} className="tabular-nums">
                #{o.id} · {o.currency} {formatNumber(o.amount)} · {o.description || '—'}
              </li>
            ))}
          </ul>
          {canEdit && (
            <button onClick={handleResync} disabled={busy}
              className="mt-1 px-3 py-1.5 text-xs rounded-lg bg-amber-900 text-white hover:bg-amber-800 disabled:opacity-50">
              {busy ? '…' : t('budget.resync')}
            </button>
          )}
        </div>
      )}

      {/* ── Rates, shown where they do damage ───────────────────────── */}
      {(staleRates.length > 0 || usedRates.size > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">{t('budget.ratesTitle')}</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
            {rates.filter(r => usedRates.has(r.currency) || r.stale).map(r => (
              <span key={r.currency} className="flex items-center gap-1.5 tabular-nums">
                <span className="font-medium text-gray-700">{r.currency}</span>
                {r.missing
                  ? <span className="text-amber-700">{t('budget.rateMissing')}</span>
                  : <>
                      <span className="text-gray-600">{formatNumber(r.rate_clp)}</span>
                      <span className="text-gray-400">{r.rate_date}</span>
                      <span className={r.official ? 'text-emerald-700' : 'text-gray-400'}
                            title={t(r.official ? 'budget.sourceOfficialHint'
                                                : 'budget.sourceMarketHint')}>
                        {t(`budget.source_${r.source_label || 'manual'}`,
                           r.source_label || '')}
                      </span>
                      {r.stale && (
                        <span className="text-amber-700">
                          {t('budget.rateStale', { days: r.age_days })}
                        </span>
                      )}
                    </>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── The orders behind the money figure ──────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-900">{t('budget.ordersTitle')}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{t('budget.ordersLegend')}</p>
        </div>
        {money.lines.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">{t('budget.noOrders')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 border-b border-gray-100">
                  <th className="text-left px-4 py-2 font-medium">#</th>
                  <th className="text-left px-3 py-2 font-medium">{t('common:tables.description', 'Detalle')}</th>
                  <th className="text-left px-3 py-2 font-medium">{t('budget.orderState')}</th>
                  <th className="text-right px-3 py-2 font-medium">{t('budget.orderOriginal')}</th>
                  <th className="text-right px-3 py-2 font-medium">{t('budget.orderRate')}</th>
                  <th className="text-right px-3 py-2 font-medium">{t('budget.orderNet')}</th>
                  <th className="text-right px-3 py-2 font-medium">{t('budget.orderVat')}</th>
                  <th className="text-right px-4 py-2 font-medium">{t('budget.orderAmount')}</th>
                </tr>
              </thead>
              <tbody>
                {money.lines.map(line => (
                  <tr key={line.entry_id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-2 text-gray-400 tabular-nums">
                      {line.purchase_id != null ? `#${line.purchase_id}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-700 max-w-xs truncate"
                        title={line.description || ''}>
                      {line.description || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded ${STATE_STYLES[line.state] || 'bg-gray-100 text-gray-500'}`}>
                        {line.state}
                      </span>
                      <span className="ml-1.5 text-gray-400">
                        {t(line.bucket === 'spent' ? 'budget.bucketSpent' : 'budget.bucketCommitted')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500 tabular-nums">
                      {line.source_currency === 'CLP'
                        ? '—'
                        : `${line.source_currency} ${formatNumber(line.source_amount)}`}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {line.source_currency === 'CLP' ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <span
                          className={line.rate_locked ? 'text-gray-500' : 'text-amber-700'}
                          title={t(line.rate_locked
                            ? 'budget.rateSettledHint' : 'budget.rateProvisionalHint',
                            { date: line.frozen_on || '' })}
                        >
                          {formatNumber(line.fx_rate)}
                          {!line.rate_locked && ' ~'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500 tabular-nums"
                        title={line.quantity
                          ? t('budget.orderNetHint', {
                              qty: formatNumber(line.quantity, 2),
                              price: formatNumber(line.unit_price, 2),
                            })
                          : ''}>
                      {formatCLP(line.net_clp)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400 tabular-nums">
                      {line.vat_clp
                        ? formatCLP(line.vat_clp)
                        : <span title={t('budget.noVatHint')}>—</span>}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-900 font-medium tabular-nums">
                      {formatCLP(line.amount_clp)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 font-medium text-gray-700">
                  <td className="px-4 py-2" colSpan={5}>{t('budget.used')}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCLP(money.net)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCLP(money.vat)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatCLP(money.used)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <BudgetEditModal
          projectId={projectId}
          hours={hours.budget}
          money={money.budget}
          onClose={() => setEditing(false)}
          onSaved={(fresh) => { setData(fresh); setEditing(false) }}
        />
      )}
    </div>
  )
}

function BudgetEditModal({ projectId, hours, money, onClose, onSaved }) {
  const { t } = useTranslation(['engineering', 'common'])
  const [hoursValue, setHoursValue] = useState(hours == null ? '' : String(hours))
  const [moneyValue, setMoneyValue] = useState(money == null ? '' : String(money))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      // An empty field means "no budget", which is not the same as zero.
      const fresh = await setProjectBudget(projectId, {
        hours_budget: hoursValue.trim() === '' ? null : Number(hoursValue),
        money_budget_clp: moneyValue.trim() === '' ? null : Number(moneyValue),
      })
      onSaved(fresh)
    } catch (err) {
      setError(err.response?.data?.detail || t('budget.saveFailed'))
      setSaving(false)
    }
  }

  return (
    <Modal title={t('budget.edit')} onClose={onClose}>
      <div className="space-y-4">
        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('budget.hoursBudgetLabel')}
          </label>
          <input type="number" min="0" step="1" autoFocus
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
            value={hoursValue} onChange={e => setHoursValue(e.target.value)}
            placeholder={t('budget.clear')} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('budget.moneyBudgetLabel')}
          </label>
          <input type="number" min="0" step="1"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
            value={moneyValue} onChange={e => setMoneyValue(e.target.value)}
            placeholder={t('budget.clear')} />
          {moneyValue.trim() !== '' && !Number.isNaN(Number(moneyValue)) && (
            <p className="text-xs text-gray-400 mt-1 tabular-nums">
              {formatCLP(Number(moneyValue))}
            </p>
          )}
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
            {saving ? t('budget.saving') : t('budget.save')}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200">
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
